import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Maximize2 } from 'lucide-react';
import YouTube from 'react-youtube';
import CategoryRow from './CategoryRow';

import { supabase } from './../../../supabaseClient';

import WatchedButton from './tabs/WatchedButton';

interface Video {
  id: string;
  title: string;
  thumbnail: string;
  sinpose?: string;
  description?: string;
  category?: string;
  url?: string;
  video_url?: string;
  trailer_url?: string;
  link_video_curto?: string;
  embed_id?: string;
  is_premium?: boolean;
  [key: string]: any;
}

interface DedicatedPlayerViewProps {
  video: Video;
  videoToPlayUrl: string;
  playerTitle: string;
  allVideos: Video[];
  onBackToDetails: () => void;
  onSelectVideo: (id: string) => void;
}

export default function DedicatedPlayerView({
  video,
  videoToPlayUrl,
  playerTitle,
  allVideos,
  onBackToDetails,
  onSelectVideo
}: DedicatedPlayerViewProps) {
  const playerContainerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // 🚀 MOTOR DE RECOMENDAÇÕES DO SUPABASE
  const [dbRelatedVideos, setDbRelatedVideos] = useState<any[]>([]);

  useEffect(() => {
    async function buscarDireto() {
      if (!video || !video.id) return;
      
      const catLower = (video.category || '').toLowerCase();
      const isGame = catLower.includes('game') || catLower.includes('jogo');

      try {
        // 1. Tenta a categoria exata primeiro
        let { data: exactData } = await supabase
          .from('videos')
          .select('*')
          .neq('id', video.id)
          .not('is_short', 'eq', true)
          .not('post_type', 'eq', 'shorts')
          .eq('category', video.category)
          .order('created_at', { ascending: false })
          .limit(10);

        let finalVideos = exactData || [];

        // 2. Se faltar, preenche com a mesma família (Jogo puxa jogo, Filme puxa filme)
        if (finalVideos.length < 10) {
          const excludeIds = [video.id, ...finalVideos.map(v => v.id)];
          
          let fallbackQuery = supabase
            .from('videos')
            .select('*')
            .not('id', 'in', `(${excludeIds.join(',')})`)
            .not('is_short', 'eq', true)
            .not('post_type', 'eq', 'shorts')
            .limit(10 - finalVideos.length);

          if (isGame) {
            fallbackQuery = fallbackQuery.or('category.ilike.%game%,category.ilike.%jogo%');
          } else {
            fallbackQuery = fallbackQuery.not('category', 'ilike', '%game%').not('category', 'ilike', '%jogo%');
          }

          const { data: fallbackData } = await fallbackQuery.order('created_at', { ascending: false });
          
          if (fallbackData) {
            finalVideos = [...finalVideos, ...fallbackData];
          }
        }

        setDbRelatedVideos(finalVideos);
      } catch (err) {
        console.error("Erro na busca direta:", err);
      }
    }
    buscarDireto();
  }, [video]);

  
  
  // 👇 INÍCIO DO PASSO 1 (Versão Otimizada Inteligente)
  const [ytPlayer, setYtPlayer] = useState<any>(null);

  useEffect(() => {
    if (!ytPlayer) return;

    const salvarTempo = () => {
      try {
        const currentTime = ytPlayer.getCurrentTime();
        const duration = ytPlayer.getDuration();
        
        // Só salva se assistiu mais de 1 minuto e se não está nos créditos finais
        if (currentTime > 60 && currentTime < (duration - 15)) {
          localStorage.setItem(`doisplay_progress_${video.id}`, currentTime.toString());
        }
      } catch (e) {}
    };

    // 1. Backup leve: salva a cada 60 segundos (não pesa nada pro navegador)
    const interval = setInterval(salvarTempo, 60000);

    // 2. Tiro certeiro: salva exatamente no milissegundo que o usuário fecha a aba ou sai do navegador
    window.addEventListener('beforeunload', salvarTempo);

    return () => {
      clearInterval(interval);
      window.removeEventListener('beforeunload', salvarTempo);
      // 3. Tiro certeiro 2: salva quando o usuário clica na setinha de "Voltar" do seu site
      salvarTempo();
    };
  }, [ytPlayer, video.id]);
  // 👆 FIM DO PASSO 1
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const extractYouTubeId = (urlOrId?: string): string => {
    if (!urlOrId) return '';
    const trimmed = String(urlOrId).trim();
    
    if (trimmed.length === 11 && !trimmed.includes('/') && !trimmed.includes('.')) {
      return trimmed;
    }
    if (trimmed.includes('youtube.com/watch?v=')) {
      const parts = trimmed.split('watch?v=');
      if (parts[1]) return parts[1].split('&')[0].split('/')[0];
    } 
    if (trimmed.includes('youtu.be/')) {
      const parts = trimmed.split('youtu.be/');
      if (parts[1]) return parts[1].split('?')[0].split('&')[0].split('/')[0];
    } 
    if (trimmed.includes('youtube.com/embed/')) {
      const parts = trimmed.split('embed/');
      if (parts[1]) return parts[1].split('?')[0].split('&')[0].split('/')[0];
    }
    
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = trimmed.match(regExp);
    if (match && match[2].length === 11) {
      return match[2];
    }
    return trimmed;
  };

  const finalRawUrl = videoToPlayUrl || video.url || video.embed_id || video.link_video_curto || video.video_url || '';
  const youtubeId = extractYouTubeId(finalRawUrl);

  const opts = {
    width: '100%',
    height: '100%',
    playerVars: {
      autoplay: 1,
      rel: 0,
      modestbranding: 1,
      controls: 1,
      fs: 1,
      iv_load_policy: 3,
      playsinline: 1, // Crucial para não pausar/abrir em fullscreen forçado no iOS/Android
      
      enablejsapi: 1,
      origin: window.location.origin
    }
  };

  const toggleFullscreenLandscape = () => {
    const element = playerContainerRef.current;
    if (!element) return;
    if (!document.fullscreenElement) {
      if (element.requestFullscreen) {
        element.requestFullscreen();
      } else if ((element as any).webkitRequestFullscreen) {
        (element as any).webkitRequestFullscreen();
      }
      if (window.screen.orientation && (window.screen.orientation as any).lock) {
        (window.screen.orientation as any).lock('landscape').catch(() => {});
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
      }
    }
  };

// 1. Remove shorts e o próprio vídeo que está tocando
 // 1. Remove shorts e o próprio vídeo que está tocando
  const cleanVideos = allVideos.filter(v => {
    if (v.id === video.id) return false;
    const isShort = v.post_type === 'shorts' || v.is_short === true || (v.category || '').toLowerCase().includes('shorts');
    return !isShort;
  });

  // 🧠 FILTRO INTELIGENTE E ESTRITO DE RELACIONADOS
  const categoryLower = (video.category || '').toLowerCase();
  const titleLower = (video.title || '').toLowerCase();
  const isCurrentTrailer = categoryLower.includes('trailer') || titleLower.includes('trailer');

  // 2. Filtra ESTRITAMENTE pela MESMA CATEGORIA exata do vídeo atual
  let relatedVideos = cleanVideos.filter(v => v.category === video.category);

  // 3. Lógica Dinâmica de Trailers:
  // Se o vídeo atual for um trailer, exige recomendações de trailers. 
  // Se for filme, bloqueia os trailers para não sujar a prateleira.
  relatedVideos = relatedVideos.filter(v => {
    const vTitle = (v.title || '').toLowerCase();
    const vCat = (v.category || '').toLowerCase();
    const isVTrailer = vTitle.includes('trailer') || vCat.includes('trailer');
    
    return isCurrentTrailer ? isVTrailer : !isVTrailer;
  });

  // 4. Fallback inteligente: Só ativa se houver menos de 5 vídeos na categoria exata.
 if (relatedVideos.length < 15) {
    const otherSameFamily = cleanVideos.filter(v => {
      const vCat = (v.category || '').toLowerCase();
      const vTitle = (v.title || '').toLowerCase();
      
      const isSameFamily = categoryLower.includes('game') || categoryLower.includes('jogo')
        ? vCat.includes('game') || vCat.includes('jogo')
        : vCat.includes('filme');
      
      const isTrailer = vTitle.includes('trailer') || vCat.includes('trailer');
      const respectsTrailerRule = isCurrentTrailer ? isTrailer : !isTrailer;
      
      return isSameFamily && respectsTrailerRule && v.category !== video.category;
    });
    
    relatedVideos = [...relatedVideos, ...otherSameFamily];
  }

  // 2. 🚀 GARANTE SEMPRE 10 VÍDEOS: Se ainda assim faltarem, preenche com qualquer vídeo do site para não ficar vazio
  if (relatedVideos.length < 10) {
    const existingIds = new Set([video.id, ...relatedVideos.map(v => v.id)]);
    const fillers = cleanVideos.filter(v => !existingIds.has(v.id));
    relatedVideos = [...relatedVideos, ...fillers];
  }

  // Corta estritamente nos 10 primeiros resultados para a prateleira
  relatedVideos = relatedVideos.slice(0, 10);

  // 🛡️ A CARTADA MÁGICA: Substitui a lista envenenada pelos vídeos perfeitos do Supabase!
  if (dbRelatedVideos && dbRelatedVideos.length > 0) {
    relatedVideos = dbRelatedVideos;
  }

  const handleRelatedClick = (videoId: string) => {
// ...
 
    const targetVideo = allVideos.find(v => v.id === videoId);
    const isShort = targetVideo && (targetVideo.post_type === 'shorts' || targetVideo.is_short === true || (targetVideo.category || '').toLowerCase().includes('shorts'));
    if (isShort) {
      window.location.href = '/shorts';
      return;
    }
    onSelectVideo(videoId);
  };

  return (
    // Colocamos position: 'relative' e diminuímos o padding do topo para o vídeo subir
    <div style={{ backgroundColor: '#000000', color: '#fff', minHeight: '100vh', padding: '10px 0 20px 0', fontFamily: 'system-ui, sans-serif', position: 'relative' }}>
      
      {/* BOTÃO FLUTUANTE COLADO NA ESQUERDA (Não ocupa espaço vertical) */}
      <div style={{ position: 'absolute', top: '2px', left: '20px', zIndex: 50 }}>
        <button 
          onClick={onBackToDetails}
          title="Voltar"
          style={{ 
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', 
            width: '30px', height: '30px', borderRadius: '50%', cursor: 'pointer', 
            backdropFilter: 'blur(4px)', transition: 'all 0.2s' 
          }}
          onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#E50914'; e.currentTarget.style.borderColor = '#E50914'; }}
          onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; }}>
          <ArrowLeft size={20} />
        </button>
      </div>

      <div style={{ maxWidth: '1100px', margin: '0 auto', width: '100%' }} ref={playerContainerRef}>
        
        {/* BLOCO DO PLAYER AJUSTADO */}
        <div
        className="player-inner-wrapper" /* 👈 ADICIONE ESTA LINHA AQUI */
          style={{
            width: '100%',
            aspectRatio: '16 / 9',
            backgroundColor: '#000',
            position: 'relative',
            overflow: 'hidden',
            borderRadius: isMobile ? '0' : '12px',
            border: isMobile ? 'none' : '1px solid #222',
            boxShadow: isMobile ? 'none' : '0 10px 40px rgba(0,0,0,0.8)'
          }}
        >
          {youtubeId ? (
            <div style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
              <YouTube
                key={youtubeId} /* 🛡️ BLINDAGEM: Impede que o player reinicie sozinho por re-renders da página */
                videoId={youtubeId}
                opts={opts}
                className="youtube-player-iframe"
                style={{ width: '100%', height: '100%', display: 'block' }}
                onReady={(e) => {
                  const player = e.target;
                  setYtPlayer(player);

                  const iframe = player.getIframe();
                  iframe.setAttribute('allowfullscreen', 'true');
                  iframe.setAttribute(
                    'allow',
                    'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                  );
                  iframe.style.width = '100%';
                  iframe.style.height = '100%';
                  iframe.style.display = 'block';
                  iframe.style.border = '0';

                  const savedTime = localStorage.getItem(`doisplay_progress_${video.id}`);
                  if (savedTime) {
                    player.seekTo(parseFloat(savedTime));
                  }
                }}
                onEnd={() => {
                  localStorage.removeItem(`doisplay_progress_${video.id}`);
                }}
              />
            </div>
          ) : (
            <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', color: '#888' }}>
              Link de vídeo indisponível.
            </div>
          )}
        </div>
        {/* FIM DO BLOCO DO PLAYER */}

        <div style={{ padding: '15px 20px 0 20px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
          
          {/* 👇 LINHA 1: Título e Etiqueta */}
          <div>
            <span style={{ backgroundColor: '#E50914', color: '#fff', fontSize: '10px', padding: '2px 6px', borderRadius: '3px', fontWeight: 'bold', textTransform: 'uppercase' }}>
              {playerTitle}
            </span>
            <h1 style={{ fontSize: '20px', fontWeight: '800', margin: '6px 0 0 0', lineHeight: '1.2' }}>{video.title}</h1>
          </div>
          
          {/* 👇 LINHA 2: Botão de Assistido (Esquerda) e Botão de Expandir (Direita) */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            
            {/* Lado Esquerdo */}
            <WatchedButton videoId={video.id} />
            
            {/* Lado Direito */}
            {isMobile && (
              <button 
                onClick={toggleFullscreenLandscape}
                title="Expandir Tela Cheia (Paisagem)"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backgroundColor: '#1f1f1f', color: '#fff', border: '1px solid #333',
                  width: '35px', height: '35px', borderRadius: '8px', cursor: 'pointer',
                  flexShrink: 0, transition: 'background 0.2s, border-color 0.2s'
                }}
              >
                <Maximize2 size={18} />
              </button>
            )}
          </div>
          
        </div>
      </div>

      {relatedVideos.length > 0 && (
        <div style={{ maxWidth: '1100px', margin: '30px auto 0 auto', padding: '0 20px', borderTop: '1px solid #222', paddingTop: '20px' }}>
          <CategoryRow title="Você também pode curtir" videos={relatedVideos} onSelectVideo={handleRelatedClick} />
        </div>
      )}

    </div>
  );
}